(() => {
  'use strict';
  const tagName = 'filtrare-brewer-features';
  if (customElements.get(tagName)) return;

  class FiltrareBrewerFeatures extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      if (!this.querySelector('[data-ff-product]')) {
        document.addEventListener('DOMContentLoaded', () => this.mount(), {
          once: true, signal: this.controller.signal,
        });
        return;
      }
      this.mount();
    }

    mount() {
      if (this.mounted || !this.isConnected) return;
      try {
        this.product = JSON.parse(this.querySelector('[data-ff-product]').textContent);
      } catch (error) {
        console.warn('Filtrare: feature product data could not be read.', error);
        return;
      }
      this.mounted = true;
      this.colorIndex = Number(this.product.colorOptionPosition || this.dataset.colorOptionPosition) - 1;
      this.variants = this.product.variants || [];
      this.buttons = [...this.querySelectorAll('button[data-ff-color]')];
      this.slots = [...this.querySelectorAll('[data-ff-media-slot]')];
      this.templates = [...this.querySelectorAll('template[data-ff-media-template]')];
      this.activeColor = null;
      this.videoStates = new Map();
      this.animationStates = new Map();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.controller.signal;

      this.carousel = this.querySelector('[data-ff-carousel]');
      if (this.carousel) {
        this.carouselCards = [...this.carousel.querySelectorAll('.fbf__card')];
        this.carouselPrev = this.querySelector('[data-ff-carousel-prev]');
        this.carouselNext = this.querySelector('[data-ff-carousel-next]');
        this.carouselProgress = this.querySelector('[data-ff-carousel-progress]');
        this.carouselMedia = window.matchMedia('(max-width: 649px)');
        this.carousel.addEventListener('scroll', () => this.scheduleCarouselUpdate(), { passive: true, signal });
        this.carouselPrev?.addEventListener('click', () => this.moveCarousel(-1), { signal });
        this.carouselNext?.addEventListener('click', () => this.moveCarousel(1), { signal });
        this.carouselMedia.addEventListener('change', () => this.updateCarousel(), { signal });
        if ('ResizeObserver' in window) {
          this.carouselResizeObserver = new ResizeObserver(() => this.scheduleCarouselUpdate());
          this.carouselResizeObserver.observe(this.carousel);
        } else {
          window.addEventListener('resize', () => this.scheduleCarouselUpdate(), { signal });
        }
        this.updateCarousel();
      }

      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-ff-color]');
        if (!button || !this.contains(button)) return;
        this.chooseColor(button.dataset.ffColor, button);
      }, { signal });
      document.addEventListener('change', (event) => {
        const host = this.findProductInfo();
        if (!host?.contains(event.target)) return;
        if (event.target.matches('input[name="id"]')) this.finishPending();
        else if (event.target.closest('variant-selects')) this.cancelPending();
        this.scheduleSync();
      }, { signal });
      document.addEventListener('product-info:loaded', () => this.bindNative(), { signal });
      document.addEventListener('shopify:section:load', () => this.bindNative(), { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, { signal });
      window.addEventListener('popstate', () => this.restoreFromURL(), { signal });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.restoreFromURL();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updateVideos(), { signal });
      this.motion.addEventListener('change', () => {
        this.updateVideos();
        this.animationStates.forEach((state) => this.motion.matches ? state.pause() : state.play());
      }, { signal });

      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = this.videoStates.get(entry.target);
            if (state) state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
          });
          this.updateVideos();
        }, { threshold: [0, 0.2] });
      }

      this.buttons.forEach((button, index) => {
        button.id ||= `fbf-${this.dataset.sectionId || this.product.productId}-swatch-${index}`;
        button.disabled = false;
      });
      this.dataset.ffReady = 'true';
      this.bindNative();
      Promise.all(['product-info', 'variant-selects'].map((name) => customElements.whenDefined(name)))
        .then(() => { if (this.isConnected && this.mounted) this.bindNative(); });
    }

    findProductInfo() {
      const matches = [...document.querySelectorAll('product-info')].filter((node) =>
        String(node.dataset.productId) === String(this.product.productId || this.dataset.productId));
      return matches.find((node) => node.id.startsWith('MainProduct-')) || matches[0] || null;
    }

    carouselIndex() {
      if (!this.carouselCards?.length) return 0;
      const first = this.carouselCards[0].offsetLeft;
      return this.carouselCards.reduce((best, card, index) =>
        Math.abs(card.offsetLeft - first - this.carousel.scrollLeft) <
        Math.abs(this.carouselCards[best].offsetLeft - first - this.carousel.scrollLeft) ? index : best, 0);
    }

    scheduleCarouselUpdate() {
      if (this.carouselFrame) return;
      this.carouselFrame = requestAnimationFrame(() => {
        this.carouselFrame = 0;
        this.updateCarousel();
      });
    }

    updateCarousel() {
      if (!this.carousel) return;
      const mobile = this.carouselMedia.matches;
      const index = mobile ? this.carouselIndex() : 0;
      this.carousel.tabIndex = mobile ? 0 : -1;
      this.carouselPrev.disabled = !mobile || this.carousel.scrollLeft <= 2;
      this.carouselNext.disabled = !mobile || this.carousel.scrollLeft >= this.carousel.scrollWidth - this.carousel.clientWidth - 2;
      if (this.carouselProgress) this.carouselProgress.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.carouselCards.length).padStart(2, '0')}`;
    }

    moveCarousel(direction) {
      if (!this.carouselMedia.matches) return;
      const target = Math.max(0, Math.min(this.carouselCards.length - 1, this.carouselIndex() + direction));
      this.carousel.scrollTo({
        left: this.carouselCards[target].offsetLeft - this.carouselCards[0].offsetLeft,
        behavior: this.motion.matches ? 'instant' : 'smooth',
      });
    }

    bindNative() {
      const host = this.findProductInfo();
      if (host !== this.host) {
        this.nativeObserver?.disconnect();
        this.restoreGalleryGuard();
        this.host = host;
        if (host) {
          this.nativeObserver = new MutationObserver(() => this.scheduleSync());
          this.nativeObserver.observe(host, {
            childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'checked'],
          });
        }
      }
      this.syncFromNative();
    }

    installGalleryGuard(host) {
      this.restoreGalleryGuard();
      const gallery = host.querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      const original = gallery.setActiveMedia;
      const hadOwnMethod = Object.hasOwn(gallery, 'setActiveMedia');
      const guarded = (mediaId, prepend) => {
        this.restoreGalleryGuard();
        return this.activateGalleryWithoutPageScroll(gallery, mediaId, prepend);
      };
      gallery.setActiveMedia = guarded;
      this.guardedGallery = gallery;
      this.originalSetActiveMedia = original;
      this.galleryHadOwnMethod = hadOwnMethod;
      this.guardedSetActiveMedia = guarded;
    }

    restoreGalleryGuard() {
      if (this.guardedGallery && this.guardedGallery.setActiveMedia === this.guardedSetActiveMedia) {
        if (this.galleryHadOwnMethod) this.guardedGallery.setActiveMedia = this.originalSetActiveMedia;
        else delete this.guardedGallery.setActiveMedia;
      }
      this.guardedGallery = null;
      this.originalSetActiveMedia = null;
      this.guardedSetActiveMedia = null;
    }

    activateGalleryWithoutPageScroll(gallery, mediaId, prepend) {
      const viewer = gallery.elements?.viewer;
      const thumbnails = gallery.elements?.thumbnails;
      const activeMedia = viewer?.querySelector(`[data-media-id="${mediaId}"]`)
        || viewer?.querySelector('[data-media-id]');
      if (!activeMedia) return;
      viewer.querySelectorAll('[data-media-id]').forEach((item) => item.classList.remove('is-active'));
      activeMedia.classList.add('is-active');
      const thumbnail = thumbnails?.querySelector(`[data-target="${mediaId}"]`);
      if (prepend) {
        if (activeMedia.parentElement.firstElementChild !== activeMedia) activeMedia.parentElement.prepend(activeMedia);
        if (thumbnail && thumbnail.parentElement.firstElementChild !== thumbnail) thumbnail.parentElement.prepend(thumbnail);
        viewer.resetPages?.();
      }
      gallery.preventStickyHeader?.();
      window.setTimeout(() => {
        if (activeMedia.isConnected && (!gallery.mql?.matches || thumbnails)) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
      });
      gallery.playActiveMedia?.(activeMedia);
      if (thumbnail) {
        gallery.setActiveThumbnail?.(thumbnail);
        gallery.announceLiveRegion?.(activeMedia, thumbnail.dataset.mediaPosition);
      }
    }

    optionGroups(picker) {
      return picker ? [...picker.querySelectorAll('fieldset, select')]
        .filter((node) => node.tagName === 'FIELDSET' || !node.closest('fieldset')) : [];
    }

    readOptions(picker) {
      return this.optionGroups(picker).map((group) => group.tagName === 'SELECT'
        ? group.value : group.querySelector('input[type="radio"]:checked')?.value);
    }

    currentOptions() {
      const options = this.readOptions(this.findProductInfo()?.querySelector('variant-selects'));
      if (options.length && options.every((option) => typeof option === 'string')) return options;
      const fromURL = new URL(window.location.href).searchParams.get('variant');
      const variant = this.variantById(fromURL) || this.variantById(this.product.selectedVariantId) || this.variants[0];
      return variant?.options?.slice() || [];
    }

    variantById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    variantForOptions(options) {
      return this.variants.find((variant) => variant.options.length === options.length
        && variant.options.every((value, index) => value === options[index]));
    }

    scheduleSync() {
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => {
        if (this.isConnected && this.mounted) this.syncFromNative();
      });
    }

    syncFromNative() {
      const options = this.currentOptions();
      const color = options[this.colorIndex] || this.product.defaultColor;
      this.renderColor(color);
      this.updateAvailability(options);
    }

    chooseColor(color, button) {
      const options = this.currentOptions();
      options[this.colorIndex] = color;
      const native = this.findProductInfo();
      const picker = native?.querySelector('variant-selects');
      if (this.useOriginSelection(native, picker, options, button, true)) return;
      this.navigateToVariant(options);
    }
    useOriginSelection(host, picker, options, focusTarget, preservePosition = false) {
      if (!host || !picker || typeof host.handleOptionValueChange !== 'function'
        || typeof picker.updateSelectionMetadata !== 'function') return false;
      const groups = this.optionGroups(picker);
      if (groups.length !== options.length) return false;
      const choices = groups.map((group, index) => group.tagName === 'SELECT'
        ? [...group.options].find((option) => option.value === options[index])
        : [...group.querySelectorAll('input[type="radio"]')].find((input) => input.value === options[index]));
      if (choices.some((choice) => !choice || choice.disabled || !choice.dataset.optionValueId)) return false;
      this.cancelPending();
      choices.forEach((choice, index) => {
        const group = groups[index];
        if (group.tagName === 'SELECT') {
          group.value = options[index];
          [...group.options].forEach((option) => option.toggleAttribute('selected', option === choice));
        } else {
          choice.checked = true;
        }
        if (group.tagName !== 'SELECT') picker.updateSelectionMetadata({ target: choice });
      });
      const nativeControl = groups[this.colorIndex].tagName === 'SELECT'
        ? groups[this.colorIndex] : choices[this.colorIndex];
      const target = choices[this.colorIndex];
      this.installGalleryGuard(host);
      this.pending = {
        options: options.slice(),
        button: focusTarget,
        anchorTop: preservePosition && focusTarget ? focusTarget.getBoundingClientRect().top : null,
      };
      this.setAttribute('aria-busy', 'true');
      this.renderColor(options[this.colorIndex]);
      this.updateAvailability(options);
      this.setStatus(this.message('updating', options[this.colorIndex]));
      try {
        host.handleOptionValueChange({ data: {
          event: { target: nativeControl },
          target: { id: focusTarget?.id || this.id, dataset: target.dataset },
          selectedOptionValues: choices.map((choice) => choice.dataset.optionValueId),
        } });
      } catch (error) {
        this.cancelPending();
        console.warn('Filtrare: using product-page navigation for this colour.', error);
        return false;
      }
      this.pendingTimer = window.setTimeout(() => {
        if (this.pending) this.navigateToVariant(this.pending.options);
      }, 12000);
      return true;
    }

    restoreFromURL() {
      const id = new URL(window.location.href).searchParams.get('variant');
      const variant = this.variantById(id) || this.variantById(this.product.selectedVariantId);
      if (!variant) return;
      const current = this.currentOptions();
      if (variant.options.every((value, index) => value === current[index])) {
        this.syncFromNative();
        return;
      }
      const button = this.buttons.find((item) => item.dataset.ffColor === variant.options[this.colorIndex]);
      const native = this.findProductInfo();
      if (!this.useOriginSelection(native, native?.querySelector('variant-selects'), variant.options, button)) {
        this.navigateToVariant(variant.options);
      }
    }

    navigateToVariant(options) {
      const variant = this.variantForOptions(options);
      this.cancelPending();
      if (!variant) {
        this.setStatus(this.message('no_combination'));
        this.syncFromNative();
        return;
      }
      const url = new URL(this.product.productUrl || this.dataset.productUrl || location.pathname, location.origin);
      url.searchParams.set('variant', variant.id);
      window.location.assign(url.href);
    }

    cancelPending() {
      clearTimeout(this.pendingTimer);
      this.pending = null;
      this.restoreGalleryGuard();
      this.removeAttribute('aria-busy');
    }

    finishPending() {
      const previous = this.pending;
      clearTimeout(this.pendingTimer);
      this.pending = null;
      this.removeAttribute('aria-busy');
      window.setTimeout(() => this.restoreGalleryGuard());
      if (previous) {
        const variant = this.variantForOptions(previous.options);
        this.setStatus(this.message('updated', previous.options[this.colorIndex]) + (variant?.available ? '' : ` ${this.message('unavailable')}`));
        if (previous.anchorTop !== null && previous.button?.isConnected) {
          const restorePosition = () => {
            if (!previous.button.isConnected) return;
            const shift = previous.button.getBoundingClientRect().top - previous.anchorTop;
            if (Math.abs(shift) > 1) window.scrollBy({ top: shift, behavior: 'instant' });
          };
          restorePosition();
          requestAnimationFrame(restorePosition);
        }
      }
    }

    updateAvailability(options) {
      this.buttons.forEach((button) => {
        const candidate = options.slice();
        candidate[this.colorIndex] = button.dataset.ffColor;
        const variant = this.variantForOptions(candidate);
        button.dataset.ffAvailable = variant?.available ? 'true' : 'false';
        button.setAttribute('aria-label', `${button.dataset.ffColor}${variant?.available ? '' : ` — ${this.message('unavailable')}`}`);
      });
    }

    renderColor(color) {
      if (!color || !this.buttons.some((button) => button.dataset.ffColor === color)) return;
      this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.ffColor === color)));
      this.querySelectorAll('[data-ff-color-label]').forEach((label) => { label.textContent = color; });
      if (this.activeColor === color) return;
      this.activeColor = color;
      this.slots.forEach((slot) => {
        const template = this.templates.find((item) => item.dataset.ffMediaTemplate === slot.dataset.ffMediaSlot
          && item.dataset.ffColor === color);
        if (!template) return;
        this.releaseVideos(slot);
        this.animationStates.forEach((state, element) => { if (slot.contains(element)) this.animationStates.delete(element); });
        slot.replaceChildren(template.content.cloneNode(true));
        slot.querySelectorAll('video').forEach((video) => this.registerVideo(video));
        slot.querySelectorAll('[data-ff-animation]').forEach((element) => this.registerAnimation(element));
      });
    }

    registerAnimation(element) {
      const img = element.querySelector('img');
      if (!img) return;
      const poster = document.createElement('canvas');
      poster.className = 'fbf__animation-poster';
      poster.setAttribute('role', 'img');
      poster.setAttribute('aria-label', img.alt);
      poster.hidden = true;
      const state = { paused: false, pause: () => {
        state.paused = true;
        img.loading = 'eager';
        if (img.complete && img.naturalWidth) {
          poster.width = Math.min(img.naturalWidth, 1600);
          poster.height = Math.round(poster.width * img.naturalHeight / img.naturalWidth);
          poster.getContext('2d').drawImage(img, 0, 0, poster.width, poster.height);
        }
        img.hidden = true;
        poster.hidden = false;
      }, play: () => {
        if (state.paused) {
          state.paused = false;
          img.hidden = false;
          poster.hidden = true;
        }
      }};
      element.append(poster);
      img.addEventListener('load', () => { if (state.paused) state.pause(); }, { signal: this.controller.signal });
      this.animationStates.set(element, state);
      if (this.motion.matches) state.pause();
    }

    registerVideo(video) {
      video.removeAttribute('autoplay');
      video.controls = true;
      video.preload = 'none';
      video.playsInline = true;
      const state = { visible: false, userPaused: false, managedPause: false };
      this.videoStates.set(video, state);
      video.addEventListener('pause', () => {
        if (!state.managedPause) state.userPaused = true;
        state.managedPause = false;
      }, { signal: this.controller.signal });
      video.addEventListener('play', () => { state.userPaused = false; }, { signal: this.controller.signal });
      this.videoObserver?.observe(video);
    }

    pauseVideo(video, state) {
      if (!video.paused) {
        state.managedPause = true;
        video.pause();
      }
    }

    updateVideos() {
      this.videoStates.forEach((state, video) => {
        const visible = state.visible && !document.hidden;
        if (!visible || this.motion.matches) this.pauseVideo(video, state);
        else if (this.dataset.autoplay === 'true' && !state.userPaused && video.paused) {
          video.muted = true;
          video.play()?.catch(() => {});
        }
      });
    }

    releaseVideos(container) {
      container.querySelectorAll('video').forEach((video) => {
        const state = this.videoStates.get(video);
        if (state) this.pauseVideo(video, state);
        this.videoObserver?.unobserve(video);
        this.videoStates.delete(video);
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        video.load();
      });
    }

    message(key, color = '') {
      return (this.product.strings?.[key] || '').replace('{color}', color);
    }

    setStatus(text) {
      const status = this.querySelector('[data-ff-status]');
      if (status) status.textContent = text;
    }

    disconnectedCallback() {
      this.cancelPending();
      cancelAnimationFrame(this.syncFrame);
      this.nativeObserver?.disconnect();
      this.carouselResizeObserver?.disconnect();
      cancelAnimationFrame(this.carouselFrame);
      this.restoreGalleryGuard();
      this.videoObserver?.disconnect();
      if (this.videoStates) this.releaseVideos(this);
      this.animationStates?.clear();
      this.controller?.abort();
      this.controller = null;
      this.host = null;
      this.mounted = false;
      this.buttons?.forEach((button) => { button.disabled = true; });
    }
  }

  customElements.define(tagName, FiltrareBrewerFeatures);
})();
